import { useCopySettings } from '@/features/copySettings';
import { copySettingsToWire } from '@/features/copySettings/useCopySettings.helpers';
import { type CopySettings } from '@/features/copySettings/useCopySettings.types';
import { copySettingsUpdated } from '@/lib/analytics/scribe/sessions';
import { type CopySetting } from '@/models/analytics/features/sessions';
import { Switch } from '@heidi/design-system/switch';
import { TypographyP2 } from '@heidi/design-system/typography';
import { AdaptiveMenuItem } from '@heidi/snowflake-ui/adaptive-menu';
import { Bold, Heading1, List, Table } from 'lucide-react';
import { FormattedMessage } from 'react-intl';

type CopySettingsRowProps = {
  testId: string;
  icon: React.ReactNode;
  label: React.ReactNode;
  checked: boolean;
  onToggle: () => void;
};

/**
 * One row in the copy-settings list. The whole `AdaptiveMenuItem` is the
 * click target — clicking anywhere on the row toggles the switch. The
 * switch itself is `pointer-events-none` and `aria-hidden` so it doesn't
 * compete with the row's selection handling. `event.preventDefault()` on
 * `onSelect` keeps the menu/drawer open after each toggle (it would close it
 * by default).
 */
const CopySettingsRow = ({
  testId,
  icon,
  label,
  checked,
  onToggle,
}: CopySettingsRowProps) => (
  <AdaptiveMenuItem
    onMouseDown={(event) => event.preventDefault()}
    onSelect={(event) => {
      event.preventDefault();
      onToggle();
    }}
    className="py-1.5"
    data-testid={`${testId}-row`}
  >
    {icon}
    <TypographyP2 className="flex-1">{label}</TypographyP2>
    <Switch
      checked={checked}
      tabIndex={-1}
      aria-hidden
      size="xs"
      className="pointer-events-none ms-auto"
      data-testid={`${testId}-switch`}
    />
  </AdaptiveMenuItem>
);

/** Maps each FE toggle key to its snake_case analytics `setting` value. */
const SETTING_ANALYTICS_NAME: Record<keyof CopySettings, CopySetting> = {
  headings: 'headings',
  boldAndItalics: 'bold_and_italics',
  bulletPoints: 'bullet_points',
  tables: 'tables',
};

/**
 * The orthogonal copy toggles (headings, bold/italics, bullet points,
 * tables) bound to the user-level `useCopySettings` preference. Shared by
 * the note-level `CopySettingsPanel` and the per-section copy-settings
 * submenu so both surfaces drive the same backend-persisted setting.
 * Bullet points and tables are omitted for the cohort that cannot act on them;
 * `useCopySettings` reports both as on while they are hidden.
 */
export const CopySettingsToggles = () => {
  const { settings, updateSettings, areListAndTableTogglesHidden } = useCopySettings();

  const onToggle = (key: keyof CopySettings) => () => {
    const isEnabled = !settings[key];
    const merged: CopySettings = { ...settings, [key]: isEnabled };
    updateSettings({ [key]: isEnabled });
    copySettingsUpdated({
      setting: SETTING_ANALYTICS_NAME[key],
      isEnabled,
      copySettings: copySettingsToWire(merged),
    });
  };

  return (
    <>
      <CopySettingsRow
        testId="copy-settings-headings"
        icon={<Heading1 className="text-text-secondary" />}
        label={<FormattedMessage defaultMessage="Headings" id="HpWSb45ioH" />}
        checked={settings.headings}
        onToggle={onToggle('headings')}
      />
      <CopySettingsRow
        testId="copy-settings-bold-italics"
        icon={<Bold className="text-text-secondary" />}
        label={<FormattedMessage defaultMessage="Bold & italics" id="n3g+/V1sEi" />}
        checked={settings.boldAndItalics}
        onToggle={onToggle('boldAndItalics')}
      />
      {!areListAndTableTogglesHidden && (
        <>
          <CopySettingsRow
            testId="copy-settings-bullet-points"
            icon={<List className="text-text-secondary" />}
            label={<FormattedMessage defaultMessage="Bullet points" id="/+P7vroKuF" />}
            checked={settings.bulletPoints}
            onToggle={onToggle('bulletPoints')}
          />
          <CopySettingsRow
            testId="copy-settings-tables"
            icon={<Table className="text-text-secondary" />}
            label={<FormattedMessage defaultMessage="Tables" id="b7YdZ/S1PN" />}
            checked={settings.tables}
            onToggle={onToggle('tables')}
          />
        </>
      )}
    </>
  );
};
